import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Trash2 } from 'lucide-react';
import { type Control, Controller, useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';

import { mensagemDeErro } from '@/lib/erros';
import { SelectPesquisavel } from '@/ui/components/select-pesquisavel';
import { SecaoFormulario } from '@/ui/patterns/secao-formulario';
import { toast } from '@/ui/primitives/aviso';
import { Button } from '@/ui/primitives/button';
import { Checkbox } from '@/ui/primitives/checkbox';
import { Input } from '@/ui/primitives/input';
import { MAXIMO_OPCOES_SELECT, type OpcaoSelect, Select } from '@/ui/primitives/select';

export interface ColunaLista {
  nome: string;
  rotulo: string;
  tipo: 'texto' | 'data' | 'select' | 'marcador';
  opcoes?: readonly OpcaoSelect[];
  /** Classe de largura da coluna na grade. */
  largura?: string;
  mono?: boolean;
}

interface ListaEditavelProps<T extends Record<string, unknown>> {
  titulo: string;
  descricao: string;
  item: z.ZodType<T>;
  colunas: readonly ColunaLista[];
  valores: readonly T[];
  novoItem: T;
  podeEditar: boolean;
  salvar: (itens: T[]) => Promise<unknown>;
  salvando: boolean;
}

interface Formulario {
  itens: Record<string, unknown>[];
}

/**
 * Edição de uma lista do cadastro (regimes, IEs, CNAEs, códigos) inteira de uma vez — o server
 * substitui a lista (PUT). Validação pelo mesmo schema do contrato.
 */
/** Lista curta e fixa → Select; longa (ex.: as 27 UFs) → SelectPesquisavel (regra do design system). */
function CampoEscolha(p: {
  invalido: { 'aria-invalid'?: true };
  rotulo: string;
  opcoes: readonly OpcaoSelect[];
  valor: string | null;
  aoMudar: (valor: string | null) => void;
}) {
  if (p.opcoes.length > MAXIMO_OPCOES_SELECT) {
    return (
      <SelectPesquisavel
        {...p.invalido}
        aria-label={p.rotulo}
        rotuloBusca={`Buscar ${p.rotulo}`}
        opcoes={p.opcoes}
        valor={p.valor}
        aoMudar={p.aoMudar}
      />
    );
  }
  return (
    <Select
      {...p.invalido}
      aria-label={p.rotulo}
      opcoes={p.opcoes}
      valor={p.valor ?? ''}
      aoMudar={p.aoMudar}
    />
  );
}

export function ListaEditavel<T extends Record<string, unknown>>(props: ListaEditavelProps<T>) {
  const { titulo, descricao, colunas, podeEditar, salvando } = props;
  const form = useForm<Formulario>({
    resolver: zodResolver(
      z.object({ itens: z.array(props.item) }) as unknown as z.ZodType<Formulario, Formulario>,
    ),
    values: { itens: props.valores as Record<string, unknown>[] },
  });
  const linhas = useFieldArray({ control: form.control, name: 'itens' });
  const erroGeral = form.formState.errors.itens;

  const enviar = form.handleSubmit(async ({ itens }) => {
    try {
      await props.salvar(itens as T[]);
      // Forma neutra: o título pode ser feminino ou plural ("Inscrições estaduais", "CNAEs").
      toast.success(`${titulo}: alterações salvas`);
    } catch (erro) {
      toast.error(mensagemDeErro(erro));
    }
  });

  return (
    <SecaoFormulario
      titulo={titulo}
      descricao={descricao}
      aoEnviar={() => void enviar()}
      acoes={
        podeEditar &&
        form.formState.isDirty && (
          <>
            <Button
              variante="fantasma"
              onClick={() => {
                form.reset();
              }}
            >
              Descartar
            </Button>
            <Button type="submit" variante="primario" carregando={salvando}>
              Salvar
            </Button>
          </>
        )
      }
    >
      <fieldset disabled={!podeEditar} className="grid gap-2">
        <legend className="sr-only">{titulo}</legend>
        {linhas.fields.length === 0 && <p className="text-sm text-texto-fraco">Nada informado.</p>}
        {linhas.fields.map((campo, indice) => (
          <div
            key={campo.id}
            className="flex flex-wrap items-end gap-2 rounded-cartao border border-borda p-2.5 animate-surgir"
          >
            {colunas.map((coluna) => (
              <CampoLinha
                key={coluna.nome}
                coluna={coluna}
                nome={`itens.${String(indice)}.${coluna.nome}`}
                control={form.control}
              />
            ))}
            {podeEditar && (
              <Button
                variante="fantasma"
                tamanho="icone-sm"
                aria-label={`Remover linha ${String(indice + 1)}`}
                onClick={() => {
                  linhas.remove(indice);
                }}
              >
                <Trash2 aria-hidden />
              </Button>
            )}
          </div>
        ))}
        {erroGeral && (
          <p role="alert" className="text-sm text-perigo">
            Confira os campos destacados.
          </p>
        )}
        {podeEditar && (
          <Button
            tamanho="sm"
            variante="secundario"
            className="justify-self-start"
            onClick={() => {
              linhas.append(props.novoItem);
            }}
          >
            <Plus aria-hidden /> Adicionar
          </Button>
        )}
      </fieldset>
    </SecaoFormulario>
  );
}

function CampoLinha({
  coluna,
  nome,
  control,
}: {
  coluna: ColunaLista;
  nome: string;
  control: Control<Formulario>;
}) {
  return (
    <Controller
      control={control as unknown as Control}
      name={nome}
      render={({ field, fieldState }) => {
        const invalido = fieldState.error ? { 'aria-invalid': true as const } : {};
        if (coluna.tipo === 'marcador') {
          return (
            <Checkbox
              className="pb-2"
              rotulo={coluna.rotulo}
              marcado={field.value === true}
              aoMudar={field.onChange}
            />
          );
        }
        return (
          <label className={`grid gap-1 ${coluna.largura ?? 'w-44'}`}>
            <span className="text-xs text-texto-suave">{coluna.rotulo}</span>
            {coluna.tipo === 'select' ? (
              <CampoEscolha
                invalido={invalido}
                rotulo={coluna.rotulo}
                opcoes={coluna.opcoes ?? []}
                valor={(field.value as string | null) ?? null}
                aoMudar={field.onChange}
              />
            ) : (
              <Input
                {...invalido}
                type={coluna.tipo === 'data' ? 'date' : 'text'}
                className={coluna.mono ? 'font-mono' : undefined}
                value={(field.value as string | null) ?? ''}
                onChange={(e) => {
                  field.onChange(
                    e.target.value === '' && coluna.tipo === 'data' ? null : e.target.value,
                  );
                }}
                onBlur={field.onBlur}
              />
            )}
          </label>
        );
      }}
    />
  );
}
